import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  X,
  Search,
  RotateCcw,
} from 'lucide-react';
import './LibraryPage.css';

const LibraryPage = () => {
  // State management
  const [activeTab, setActiveTab] = useState('books');
  const [books, setBooks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('addBook'); // addBook, editBook, issueBook, returnBook
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    isbn: '',
    category: '',
    totalCopies: '',
    availableCopies: '',
    bookId: '',
    studentName: '',
  });

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [activeTab, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'books') {
        const response = await api.get('/library/books');
        const data = response.data.items || response.data;
        setBooks(Array.isArray(data) ? data : []);
      } else {
        const response = await api.get('/library/issues');
        let issuesList = response.data.items || response.data;
        issuesList = Array.isArray(issuesList) ? issuesList : [];
        if (statusFilter !== 'all') {
          issuesList = issuesList.filter((issue) => issue.status === statusFilter);
        }
        setIssues(issuesList);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      if (activeTab === 'books') setBooks([]);
      else setIssues([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBook = () => {
    setModalType('addBook');
    setEditingId(null);
    setFormData({
      title: '',
      author: '',
      isbn: '',
      category: '',
      totalCopies: '',
      availableCopies: '',
      bookId: '',
      studentName: '',
    });
    setShowModal(true);
  };

  const handleEditBook = (book) => {
    setModalType('editBook');
    setEditingId(book.id);
    setFormData({
      title: book.title || '',
      author: book.author || '',
      isbn: book.isbn || '',
      category: book.category || '',
      totalCopies: book.totalCopies || '',
      availableCopies: book.availableCopies || '',
      bookId: '',
      studentName: '',
    });
    setShowModal(true);
  };

  const handleIssueBook = () => {
    setModalType('issueBook');
    setEditingId(null);
    setFormData({
      title: '',
      author: '',
      isbn: '',
      category: '',
      totalCopies: '',
      availableCopies: '',
      bookId: '',
      studentName: '',
    });
    setShowModal(true);
  };

  const handleReturnBook = async (issueId) => {
    try {
      await api.put(`/library/issues/${issueId}/return`, {});
      setIssues(issues.map((issue) => (issue.id === issueId ? { ...issue, status: 'returned' } : issue)));
    } catch (err) {
      console.error('Error returning book:', err);
      setError('Failed to return book');
    }
  };

  const handleDeleteBook = async (bookId) => {
    try {
      await api.delete(`/library/books/${bookId}`);
      setBooks(books.filter((b) => b.id !== bookId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting book:', err);
      setError('Failed to delete book');
    }
  };

  const handleSaveBook = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        title: formData.title,
        author: formData.author,
        isbn: formData.isbn,
        category: formData.category,
        totalCopies: parseInt(formData.totalCopies),
        availableCopies: parseInt(formData.availableCopies),
      };

      if (editingId) {
        await api.put(`/library/books/${editingId}`, payload);
      } else {
        await api.post('/library/books', payload);
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error saving book:', err);
      setError('Failed to save book');
    }
  };

  const handleIssueBookSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        bookId: formData.bookId,
        studentName: formData.studentName,
      };

      await api.post('/library/issues', payload);
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error issuing book:', err);
      setError('Failed to issue book');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'issued':
        return '#dc2626';
      case 'returned':
        return '#22c55e';
      case 'overdue':
        return '#f472b6';
      default:
        return '#6b7280';
    }
  };

  // Stats
  const totalBooks = books.reduce((sum, b) => sum + (b.totalCopies || 0), 0);
  const totalIssued = issues.filter((i) => i.status === 'issued').length;
  const totalAvailable = books.reduce((sum, b) => sum + (b.availableCopies || 0), 0);
  const overdueCount = issues.filter((i) => i.status === 'overdue').length;

  // Search filter for books
  const filteredBooks = books.filter(
    (book) =>
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.isbn && book.isbn.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: '#111827' }}>
            Library Management
          </h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Manage books and issue tracking</p>
        </div>

        {/* Stats Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Total Books
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#111827' }}>
              {totalBooks}
            </p>
          </div>
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Issued
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>
              {totalIssued}
            </p>
          </div>
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Available
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>
              {totalAvailable}
            </p>
          </div>
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280', textTransform: 'uppercase' }}>
              Overdue
            </p>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#f472b6' }}>
              {overdueCount}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            borderBottom: '1px solid #e5e7eb',
            marginBottom: '24px',
          }}
        >
          <button
            onClick={() => setActiveTab('books')}
            style={{
              padding: '12px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'books' ? '600' : '500',
              color: activeTab === 'books' ? '#3b82f6' : '#6b7280',
              borderBottom: activeTab === 'books' ? '2px solid #3b82f6' : 'none',
            }}
          >
            Books
          </button>
          <button
            onClick={() => setActiveTab('issues')}
            style={{
              padding: '12px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === 'issues' ? '600' : '500',
              color: activeTab === 'issues' ? '#3b82f6' : '#6b7280',
              borderBottom: activeTab === 'issues' ? '2px solid #3b82f6' : 'none',
            }}
          >
            Issues
          </button>
        </div>

        {/* Books Tab */}
        {activeTab === 'books' && (
          <div>
            {/* Filter Bar */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
                <Search
                  size={18}
                  style={{
                    position: 'absolute',
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                  }}
                />
                <input
                  type="text"
                  placeholder="Search books by title, author, or ISBN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <button
                onClick={handleAddBook}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Plus size={18} />
                Add Book
              </button>
            </div>

            {/* Books Table */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                overflow: 'x-auto',
              }}
            >
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  Loading books...
                </div>
              ) : filteredBooks.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6b7280',
                  }}
                >
                  <BookOpen size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
                  <h3 style={{ margin: '0 0 8px 0', color: '#9ca3af' }}>No books found</h3>
                  <p style={{ margin: 0, fontSize: '14px' }}>Add your first book to get started</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Title
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Author
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          ISBN
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Category
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                          Total
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                          Available
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBooks.map((book) => (
                        <tr key={book.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#111827' }}>{book.title}</td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>{book.author}</td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>{book.isbn}</td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>{book.category}</td>
                          <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                            {book.totalCopies}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                            <span
                              style={{
                                backgroundColor: '#dbeafe',
                                color: '#1e40af',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              {book.availableCopies}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                onClick={() => handleEditBook(book)}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: '#f3f4f6',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(book.id)}
                                style={{
                                  padding: '6px 10px',
                                  backgroundColor: '#fee2e2',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Trash2 size={16} style={{ color: '#dc2626' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Issues Tab */}
        {activeTab === 'issues' && (
          <div>
            {/* Filter Bar */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="all">All Status</option>
                <option value="issued">Issued</option>
                <option value="overdue">Overdue</option>
                <option value="returned">Returned</option>
              </select>

              <button
                onClick={handleIssueBook}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginLeft: 'auto',
                }}
              >
                <Plus size={18} />
                Issue Book
              </button>
            </div>

            {/* Issues Table */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                overflow: 'x-auto',
              }}
            >
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  Loading issues...
                </div>
              ) : issues.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6b7280',
                  }}
                >
                  <BookOpen size={48} style={{ margin: '0 auto 16px', color: '#d1d5db' }} />
                  <h3 style={{ margin: '0 0 8px 0', color: '#9ca3af' }}>No issues found</h3>
                  <p style={{ margin: 0, fontSize: '14px' }}>Issue your first book to get started</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Book Title
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Student Name
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Issue Date
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Due Date
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Return Date
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Status
                        </th>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                          Fine
                        </th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600' }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {issues.map((issue) => (
                        <tr key={issue.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#111827' }}>
                            {issue.bookTitle || issue.book?.title}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                            {issue.studentName || issue.student?.name}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                            {issue.issueDate}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                            {issue.dueDate}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#6b7280' }}>
                            {issue.returnDate || '-'}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span
                              style={{
                                backgroundColor:
                                  issue.status === 'issued'
                                    ? '#fee2e2'
                                    : issue.status === 'overdue'
                                    ? '#fbcfe8'
                                    : '#dcfce7',
                                color: getStatusColor(issue.status),
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                textTransform: 'capitalize',
                              }}
                            >
                              {issue.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                            {issue.fine ? `₹${issue.fine}` : '-'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {issue.status === 'issued' || issue.status === 'overdue' ? (
                              <button
                                onClick={() => handleReturnBook(issue.id)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#22c55e',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <RotateCcw size={14} />
                                Return
                              </button>
                            ) : (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '400px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Delete Book?
              </h2>
              <p style={{ margin: '0 0 16px 0', color: '#6b7280' }}>
                This action cannot be undone. Are you sure you want to delete this book?
              </p>
            </div>
            <div
              style={{
                padding: '16px 24px',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                borderTop: '1px solid #e5e7eb',
              }}
            >
              <button
                onClick={() => setDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteBook(deleteConfirm)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Book Modal */}
      {showModal && (modalType === 'addBook' || modalType === 'editBook') && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                {editingId ? 'Edit Book' : 'Add New Book'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveBook}>
              <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    placeholder="Enter book title"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Author
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleFormChange}
                    placeholder="Enter author name"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      ISBN
                    </label>
                    <input
                      type="text"
                      name="isbn"
                      value={formData.isbn}
                      onChange={handleFormChange}
                      placeholder="Enter ISBN"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Category
                    </label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleFormChange}
                      placeholder="Enter category"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Total Copies
                    </label>
                    <input
                      type="number"
                      name="totalCopies"
                      value={formData.totalCopies}
                      onChange={handleFormChange}
                      placeholder="Enter total copies"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                      Available Copies
                    </label>
                    <input
                      type="number"
                      name="availableCopies"
                      value={formData.availableCopies}
                      onChange={handleFormChange}
                      placeholder="Enter available copies"
                      required
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  {editingId ? 'Update Book' : 'Add Book'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Issue Book Modal */}
      {showModal && modalType === 'issueBook' && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
              maxWidth: '500px',
              width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                Issue Book
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleIssueBookSubmit}>
              <div style={{ padding: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Select Book
                  </label>
                  <select
                    name="bookId"
                    value={formData.bookId}
                    onChange={handleFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">Choose a book</option>
                    {books
                      .filter((b) => b.availableCopies > 0)
                      .map((book) => (
                        <option key={book.id} value={book.id}>
                          {book.title} by {book.author}
                        </option>
                      ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                    Student Name
                  </label>
                  <input
                    type="text"
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleFormChange}
                    placeholder="Enter student name"
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '16px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                >
                  Issue Book
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryPage;
